/**
 * EmailOtpStep — TREATX-PATIENT-DOCTOR-EMAIL-OTP-PRECREATE-01
 * ===========================================================
 *
 * Small reusable inline OTP input + resend control rendered on the
 * SAME page as the Patient/Doctor registration form. The parent owns
 * the request/verify lifecycle; this component is purely presentational
 * and reports back the current code + a resend trigger via callbacks.
 *
 * Critical contract:
 *   • Parent must NOT submit registration to the backend until the
 *     user has successfully entered the OTP and the parent has
 *     forwarded it as `otp_code` on the canonical register endpoint.
 *   • This component never POSTs anywhere — it only collects digits
 *     and emits events.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';

const OTP_LEN = 6;

export const EmailOtpStep = ({
  email,
  cooldownSeconds = 60,
  expiresInSeconds = 600,
  loading = false,
  errorMsg = '',
  onVerify,
  onResend,
  onBack,
  testIdPrefix = 'otp',
  // TREATX-P1-OTP-03 — Optional override for the middle description
  // line. When supplied, replaces the default claim "We sent a 6-digit
  // verification code to <email>." Used by ForgotPasswordPage to render
  // anti-enumeration-safe copy that never asserts a dispatch happened.
  description = null,
}) => {
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const canResend = secondsLeft <= 0;

  const handleChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, OTP_LEN);
    setCode(v);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length !== OTP_LEN || loading) return;
    onVerify(code);
  };

  const handleResend = async () => {
    if (!canResend || loading) return;
    setCode('');
    await onResend();
    setSecondsLeft(cooldownSeconds);
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-step`}>
      <div className="flex justify-center">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mail className="h-6 w-6 text-primary" />
        </div>
      </div>
      <div className="text-center">
        <h2 className="text-xl font-bold text-slate-800 mb-1">Verify your email</h2>
        <p className="text-sm text-slate-600" data-testid={`${testIdPrefix}-description`}>
          {description ?? (
            <>We sent a 6-digit verification code to <strong>{email}</strong>.</>
          )}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          Enter the code below to continue. The code expires in {Math.round(expiresInSeconds / 60)} minutes.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3" data-testid={`${testIdPrefix}-form`}>
        <Input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={OTP_LEN}
          placeholder="• • • • • •"
          value={code}
          onChange={handleChange}
          disabled={loading}
          className="text-center text-2xl tracking-[0.5em] font-bold h-14"
          data-testid={`${testIdPrefix}-input`}
        />

        {errorMsg ? (
          <div
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            data-testid={`${testIdPrefix}-error`}
          >
            {errorMsg}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={code.length !== OTP_LEN || loading}
          className="w-full h-11"
          data-testid={`${testIdPrefix}-verify-button`}
        >
          {loading ? 'Verifying…' : 'Verify & continue'}
        </Button>

        <div className="flex items-center justify-between text-xs">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="inline-flex items-center text-slate-500 hover:text-slate-700 disabled:opacity-50"
              data-testid={`${testIdPrefix}-back`}
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />
              Use a different email
            </button>
          ) : <span />}

          <button
            type="button"
            onClick={handleResend}
            disabled={!canResend || loading}
            className="inline-flex items-center text-primary hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
            data-testid={`${testIdPrefix}-resend`}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            {canResend ? 'Resend code' : `Resend in ${secondsLeft}s`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailOtpStep;
